import React, { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

const LocationModal = ({ mshow, handleClose, userCity, createShop, setCreateShop }) => {
  const [loading, setLoading] = useState(false);
  const [pickedLocation, setPickedLocation] = useState(null); // { lat, lng, address }
  const [modalEntered, setModalEntered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const city = userCity || "Lahore"; // Default fallback if no city

  useEffect(() => {
    if (!mshow) {
      setModalEntered(false);
      // Cleanup map when modal closes
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markerRef.current = null;
      setPickedLocation(null);
      setSearchQuery("");
      setSearchError("");
      return;
    }

    // Load Leaflet assets dynamically if not already loaded
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    if (!document.getElementById("leaflet-js")) {
      const script = document.createElement("script");
      script.id = "leaflet-js";
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      document.body.appendChild(script);
    }
  }, [mshow]);

  useEffect(() => {
    if (mshow && modalEntered) {
      if (window.L) {
        initMapFlow();
      } else {
        const checkLeaflet = setInterval(() => {
          if (window.L) {
            clearInterval(checkLeaflet);
            initMapFlow();
          }
        }, 100);
        return () => clearInterval(checkLeaflet);
      }
    }
  }, [mshow, modalEntered]);

  const initMapFlow = async () => {
    setLoading(true);
    try {
      // 1. Geocode user's city zone using OSM Nominatim
      const geocodeRes = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)}&format=json&limit=1`
      );
      const geocodeData = await geocodeRes.json();

      let lat = 31.5204; // Lahore fallback lat
      let lon = 74.3587; // Lahore fallback lon

      if (geocodeData && geocodeData.length > 0) {
        lat = parseFloat(geocodeData[0].lat);
        lon = parseFloat(geocodeData[0].lon);
      }

      // 2. Initialize Leaflet Map
      if (!window.L) return;
      const L = window.L;

      const mapContainer = document.getElementById("leaflet-picker-map");
      if (!mapContainer) return;

      // Create map instance
      const map = L.map("leaflet-picker-map", {
        center: [lat, lon],
        zoom: 13,
      });
      mapRef.current = map;

      // Invalidate map size to ensure all tiles render correctly once dimensions are set
      setTimeout(() => {
        map.invalidateSize();
      }, 100);

      // Add OpenStreetMap tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors',
      }).addTo(map);

      // 3. Pre-populate marker if createShop has coordinates
      let initialMarkerLat = lat;
      let initialMarkerLng = lon;
      if (createShop?.latitude && createShop?.longitude) {
        initialMarkerLat = createShop.latitude;
        initialMarkerLng = createShop.longitude;
        // Center the map on the existing coordinates if they exist
        map.setView([initialMarkerLat, initialMarkerLng], 13);
      }

      // Add marker to map
      const marker = L.marker([initialMarkerLat, initialMarkerLng], { draggable: false }).addTo(map);
      markerRef.current = marker;

      // Get initial address
      await fetchAddress(initialMarkerLat, initialMarkerLng);

      // 4. Map click event listener to update marker location
      map.on("click", async (e) => {
        const clickedLatLng = e.latlng;
        marker.setLatLng(clickedLatLng);
        setSearchError("");
        await fetchAddress(clickedLatLng.lat, clickedLatLng.lng);
      });

    } catch (err) {
      console.error("Map initialization failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddress = async (lat, lng) => {
    try {
      const reverseRes = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`
      );
      const data = await reverseRes.json();
      if (data && data.display_name) {
        setPickedLocation({
          lat,
          lng,
          address: data.display_name,
        });
      }
    } catch (err) {
      console.error("Address lookup failed:", err);
    }
  };

  // Search bar: geocode whatever the user types (city, country, address, landmark)
  // and recenter the map + marker on the top match.
  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query || !mapRef.current || !window.L) return;

    setSearching(true);
    setSearchError("");

    try {
      const searchRes = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`
      );
      const searchData = await searchRes.json();

      if (searchData && searchData.length > 0) {
        const lat = parseFloat(searchData[0].lat);
        const lng = parseFloat(searchData[0].lon);

        mapRef.current.setView([lat, lng], 13);

        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng]);
        } else {
          markerRef.current = window.L.marker([lat, lng]).addTo(mapRef.current);
        }

        await fetchAddress(lat, lng);
      } else {
        setSearchError("No matching location found. Try a different search.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = () => {
    if (pickedLocation && setCreateShop) {
      setCreateShop((prev) => ({
        ...prev,
        shopaddress: pickedLocation.address,
        companyaddress: pickedLocation.address,
        address: pickedLocation.address,
        latitude: pickedLocation.lat,
        longitude: pickedLocation.lng,
      }));
    }
    handleClose();
  };

  return (
    <Modal
      show={mshow}
      onHide={handleClose}
      size="lg"
      centered
      onEntered={() => setModalEntered(true)}
    >
      <Modal.Header closeButton style={{ background: "#0353a4", color: "white" }}>
        <Modal.Title className="fs-5 fw-bold text-white">Select Location</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 position-relative" style={{ minHeight: "400px" }}>
        <Form onSubmit={handleSearch} className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search for a city, country, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
            <Button
              variant="primary"
              type="submit"
              disabled={loading || searching || !searchQuery.trim()}
              style={{ background: "#0353a4", border: "none" }}
            >
              {searching ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Search"
              )}
            </Button>
          </InputGroup>
          {searchError && (
            <small className="text-danger d-block mt-1 font_12">{searchError}</small>
          )}
        </Form>

        {loading && (
          <div
            className="d-flex flex-column align-items-center justify-content-center position-absolute w-100 h-100 top-0 start-0 bg-white"
            style={{ zIndex: 1000, borderRadius: "8px" }}
          >
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2 font_13">Loading map location...</p>
          </div>
        )}
        <div
          id="leaflet-picker-map"
          style={{
            height: "400px",
            width: "100%",
            borderRadius: "8px",
            border: "1px solid #cbd5e1"
          }}
        />
        {pickedLocation && !loading && (
          <div className="mt-3 p-3 bg-light rounded border border-secondary-subtle">
            <h6 className="fw-bold text-dark mb-1 font_13">Selected Address:</h6>
            <p className="text-muted mb-0 font_12" style={{ lineHeight: "1.4" }}>{pickedLocation.address}</p>
            <small className="text-primary mt-1 d-block font_11">
              Coordinates: {pickedLocation.lat.toFixed(5)}, {pickedLocation.lng.toFixed(5)}
            </small>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!pickedLocation || loading} style={{ background: "#0353a4", border: "none" }}>
          Confirm Address
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LocationModal;